/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { MultiSelect, FormItem, FormLabel } from 'carbon-addons-iot-react';
import { useField } from 'formik';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';

import { FormLabelTooltip } from 'Common';

const propTypes = {
  id: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  onChange: PropTypes.func,
  /** Label goes on top of the select */
  label: PropTypes.string,
  /** inner label goes inside the select before the items become selected */
  innerLabel: PropTypes.string.isRequired,
  /** don't show label */
  hideLabel: PropTypes.bool,
  /** is this field required */
  required: PropTypes.bool,
  /** detailed documentation link */
  learnMore: PropTypes.string,
  /** when set to true, returns an array of strings rather than objects. (ie: ['item1', 'item2']) */
  shouldReturnSimpleValues: PropTypes.bool,
  /** when set to true, the "innerLabel" persists over the selected items */
  hideSelectedLabels: PropTypes.bool,
  /** show tooltip for field */
  tooltip: PropTypes.string,
  /** optional class name to use on the multiselect */
  className: PropTypes.string,
  /** an optional function that gets called back from carbon with parameters and should return a sorted list */
  sortItems: PropTypes.func,
  /** Should the multi select be filterable */
  isFilterable: PropTypes.bool,
};

const defaultProps = {
  disabled: false,
  onChange: noop,
  label: null,
  // have to override the sort function as it breaks the list on FireFox
  sortItems: itemsToSort => itemsToSort,
  required: false,
  hideLabel: false,
  learnMore: null,
  shouldReturnSimpleValues: false,
  hideSelectedLabels: false,
  tooltip: null,
  className: null,
  isFilterable: false,
};

/** re-maps items to the object structure for the Multiselect if needed. */
export const formatInitialItems = (initialItems, items) =>
  initialItems?.length && typeof initialItems[0] === 'string'
    ? initialItems.map(initialItem => {
        const correspondingItem = items?.find(item => item.id === initialItem);
        return correspondingItem
          ? { ...correspondingItem }
          : { id: initialItem, text: initialItem, label: initialItem };
      })
    : initialItems;

/** This assumes that you have initialized the Formik form with the initialSelectedItems */
const MultiSelectFormik = ({
  id,
  disabled,
  items,
  label,
  hideLabel,
  learnMore,
  tooltip,
  required,
  innerLabel,
  onChange,
  sortItems,
  shouldReturnSimpleValues,
  hideSelectedLabels,
  className,
  isFilterable,
  ...others
}) => {
  const [field, meta, helpers] = useField({ name: id, multiple: true });

  const initialSelectedItems = useMemo(
    () => (Array.isArray(field.value) ? formatInitialItems(field.value, items) : []),
    [field.value]
  );

  const generatedLabel = useMemo(
    () =>
      Array.isArray(field.value) && !isEmpty(field.value) && !hideSelectedLabels
        ? formatInitialItems(field.value, items)
            .map(item => item.label)
            .join(', ')
        : innerLabel,
    [field.value, hideSelectedLabels, innerLabel]
  );

  const MultiSelectComponent = isFilterable ? MultiSelect.Filterable : MultiSelect;

  return (
    <FormItem className={className}>
      {!hideLabel ? (
        <FormLabel>
          <FormLabelTooltip
            label={label}
            tooltip={tooltip}
            learnMore={learnMore}
            required={required}
          />
        </FormLabel>
      ) : null}
      <MultiSelectComponent
        compareItems={(a, b) => a.id === b.id}
        sortItems={sortItems}
        selectionFeedback="fixed"
        id={id}
        name={id}
        items={items}
        title={label}
        label={generatedLabel}
        placeholder={isFilterable ? generatedLabel : ''}
        onChange={e => {
          if (shouldReturnSimpleValues) {
            helpers.setValue(e.selectedItems.map(item => item.id));
          } else {
            field.onChange({
              type: 'change',
              target: { value: e.selectedItems, name: id },
            });
          }
          // and pass up the event in case the container components needs to react to this event in some way
          onChange({ selectedItems: e.selectedItems });
        }}
        onBlur={field.onBlur}
        initialSelectedItems={initialSelectedItems}
        invalid={meta.error && meta.touched}
        invalidText={meta.error}
        disabled={disabled}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...others}
      />
    </FormItem>
  );
};

MultiSelectFormik.propTypes = propTypes;
MultiSelectFormik.defaultProps = defaultProps;

export default MultiSelectFormik;
